import React, { ChangeEvent, Component, createRef, ReactNode } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import moment from 'moment/moment';
import { Loading } from '@trlogic/ui-pack';
import { FileUpload, FileUploadHandlerParam, ItemTemplateOptions } from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import _ from 'lodash';
import { TransactionChatLog } from '../../../../domain/case/CaseDomains';
import { FormicaProps, withFormica } from '../../../../helpers/withFormica';
import { RootState } from '../../../../redux/store';
import { addPagePath, changePagePath } from '../../../../redux/actions/PageActions';
import MessageUtil from '../../../../util/MessageUtil';
import Icon from '../../../../icons/Icon';
import CaseService from '../../../../service/CaseService';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import FormicaInput from 'components/custom/FormicaInput';
import TransactionChatAttachmentItemComponent from './TransactionChatAttachmentItemComponent';
import LanguageUtil from '../../../../util/LanguageUtil';
import { assertCanWriteChat, hasCasesChatWritePermission, notifyChatWriteNotPermitted } from '../../../../util/CaseBulkCloseUtil';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import EnvironmentUtil from '../../../../util/EnvironmentUtil';

let messagesEndRef: any = null;

class TransactionChatAttachmentComponent extends Component<Props, State> {
  public state: State = new State();

  private caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);
  private fileUploadRef = createRef<FileUpload>();
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private editInputRefs: { [key: string]: React.RefObject<HTMLInputElement> } = {};

  public render(): ReactNode {
    const { commentLoading, comment } = this.state;
    const canWriteChat: boolean = hasCasesChatWritePermission();

    return (
      <div className={'w-full h-full px-5'}>
        <div className={`flex-grow overflow-y-auto ${canWriteChat ? 'pb-10' : ''}`}>
          {this.getAttachments()}
          <div ref={(el) => (messagesEndRef = el)} />
        </div>
        {canWriteChat && (
        <div className={'flex gap-2 p-2 absolute bottom-0 left-0 w-full items-center justify-center'}>
          <FileUpload
            auto
            ref={this.fileUploadRef}
            mode='basic'
            name='file'
            maxFileSize={this.environmentUtil.getStorageMaxSize()}
            headerTemplate={this.headerTemplate}
            itemTemplate={this.itemTemplateFile}
            customUpload
            uploadHandler={this.uploadHandler}
            emptyTemplate={
              <p className='m-0'>{LanguageUtil.get({ id: 'common.label.dragToUpload', defaultMessage: 'Drag and drop files to here to upload.' })}</p>
            }
          />
          <FormicaInput value={comment} onChange={this.onChangeComment} onKeyPress={this.handleKeyPress} disabled={commentLoading} />
          <div
            className={`cursor-pointer bg-white border rounded w-[34px] h-[34px] min-h-[34px] min-w-[34px] shrink-0 flex items-center justify-center align-center text-f-purple `}
            onClick={this.sendComment}
          >
            {commentLoading ? <Loading /> : <Icon icon={'deploy'} size={20} color={'#284564'} />}
          </div>
        </div>
        )}
      </div>
    );
  }

  public componentDidMount(): void {
    this.scrollToBottom();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (!_.isEqual(prevProps.chats, this.props.chats)) {
      this.scrollToBottom();
    }
    if (!_.isEqual(prevState.editComment, this.state.editComment)) {
      Object.keys(this.state.editComment).forEach((key) => {
        if (this.state.editComment[key] && this.editInputRefs[key]) {
          this.editInputRefs[key].current?.focus();
        }
      });
    }
  }

  private scrollToBottom = () => {
    messagesEndRef.scrollIntoView({ behavior: 'smooth' });
  };

  private getAttachments = () => {
    const { editComment } = this.state;
    const { chats, user } = this.props;
    const canWriteChat: boolean = hasCasesChatWritePermission();
    return (
      chats &&
      chats.length > 0 &&
      chats.map((attachment: TransactionChatLog) => {
        if (attachment.type == 'COMMENT') {
          const isEditing = editComment[attachment.id];
          if (!this.editInputRefs[attachment.id]) {
            this.editInputRefs[attachment.id] = createRef();
          }

          return (
            <div
              key={attachment.id || attachment.timestamp}
              className={`flex rounded-[10px] w-2/3 border bg-white p-2 my-4 items-center gap-2 ${
                user.username === attachment.username ? 'ml-auto' : 'mr-auto'
              }`}
            >
              <div className={'flex flex-col flex-grow gap-y-1'}>
                <div className={'flex justify-between items-center'}>
                  <div className={'flex gap-x-1 items-center'}>
                    <div className={'flex justify-center align-center bg-[#D7E5FF] rounded-full w-[26px] h-[26px] min-h-[26px] min-w-[26px] shrink-0 '}>
                      <div className={'text-f-purple'}>
                        <Icon icon={'people'} size={16} color={'white'} />
                      </div>
                    </div>
                    <div className={'flex-grow text-xs text-[#6CA2FF]'}>{attachment.username}</div>
                  </div>
                  <div className={'text-[#333333] text-2xs font-extralight'}>{moment(attachment.timestamp).format('LLLL').valueOf()}</div>
                </div>
                <div className={'flex justify-between items-center gap-x-2'}>
                  <div className={'flex-grow overflow-auto text-[#4F4F4F] text-md'}>
                    {isEditing ? (
                      <input
                        defaultValue={attachment.content}
                        ref={this.editInputRefs[attachment.id]}
                        className={'w-full border border rounded px-1'}
                        onBlur={(event) => this.onBlurComment(event, attachment.id, attachment)}
                      />
                    ) : (
                      attachment.content
                    )}
                  </div>
                  <div>
                    {canWriteChat && (
                      <Icon icon={'edit'} size={16} color={'black'} className={'cursor-pointer'} onClick={() => this.editComment(attachment.id)} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        } else if (attachment.type == 'ATTACHMENT') {
          return (
            <div
              key={attachment?.id}
              className={`flex rounded-[10px] w-2/3 border bg-white p-2 my-4 items-center gap-2 ${
                user.username === attachment.username ? 'ml-auto' : 'mr-auto'
              }`}
            >
              <div className={'flex flex-col flex-grow gap-y-1'}>
                <div className={'flex justify-between items-center'}>
                  <div className={'flex gap-x-1 items-center'}>
                    <div className={'flex justify-center align-center bg-[#D7E5FF] rounded-full w-[26px] h-[26px] min-h-[26px] min-w-[26px] shrink-0 '}>
                      <div className={'text-f-purple'}>
                        <Icon icon={'file'} size={16} color={'white'} />
                      </div>
                    </div>
                    <div className={'flex-grow text-xs text-[#6CA2FF]'}>
                      <span className={'text-[#6CA2FF] text-sm font-normal'}>{attachment.username}</span>
                    </div>
                  </div>
                  <div className={'text-[#333333] text-2xs font-extralight'}>{moment(attachment.timestamp).format('LLLL').valueOf()}</div>
                </div>
                <TransactionChatAttachmentItemComponent key={attachment?.id} attachment={attachment} />
              </div>
            </div>
          );
        }
      })
    );
  };

  private itemTemplateFile = (file: any, options: ItemTemplateOptions): React.ReactNode => {
    return (
      <div className='flex items-center flex-wrap'>
        <span className='flex text-left ml-3'>{file.name}</span>
        <small className='flex text-left mx-3'>{new Date().toLocaleDateString()}</small>
        <Tag value={options.formatSize} severity='warning' className='flex px-3 py-2' />
        <Button type='button' icon='pi pi-times' className='flex p-button-outlined p-button-rounded p-button-danger ml-auto' onClick={options.onRemove} />
      </div>
    );
  };

  private headerTemplate = (options: any) => {
    const { className, chooseButton, uploadButton, cancelButton } = options;
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center' }}>
        {chooseButton}
        {uploadButton}
        {cancelButton}
      </div>
    );
  };

  private sendComment = async () => {
    const { comment } = this.state;
    const { domain } = this.props;
    if (!assertCanWriteChat()) {
      return;
    }
    if (comment == undefined || comment.trim().length == 0 || domain == undefined) return;
    this.setState({ commentLoading: true });
    try {
      await this.caseService.commentDomain(domain, comment);
      this.setState({ comment: '' });
    } catch (e) {
      const isUnauthorizedResponse: boolean =
        typeof e === 'object' && e != null && 'response' in e &&
        (e as { response?: { status?: number } }).response?.status === 401;
      if (isUnauthorizedResponse) {
        notifyChatWriteNotPermitted();
      }
      console.error(e);
    } finally {
      this.setState({ commentLoading: false });
    }
  };

  private uploadHandler = async (e: FileUploadHandlerParam) => {
    const { domain } = this.props;
    if (!assertCanWriteChat() || domain == undefined) return;
    try {
      await this.caseService.attachmentDomain(domain, e.files[0]);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'messages.success.fileUpload' }),
        detail: <div></div>,
      });
    } catch (e) {
      console.error(e);
    }
    if (this.fileUploadRef.current) {
      this.fileUploadRef.current.clear();
    }
  };

  private onChangeComment = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ comment: event.target.value });
  };

  private editComment = (id: string) => {
    if (!hasCasesChatWritePermission()) {
      return;
    }
    this.setState((prevState) => ({
      editComment: {
        ...prevState.editComment,
        [id]: !prevState.editComment[id],
      },
    }));
  };

  private onBlurComment = async (event: ChangeEvent<HTMLInputElement>, id: string, attachment: TransactionChatLog) => {
    const { domain } = this.props;
    if (!assertCanWriteChat() || domain == undefined) return;

    const oldContent = attachment.content;
    const newContent = event.target.value;
    attachment.content = newContent;

    try {
      await this.caseService.commentEditDomain(domain, event.target.value, id);
      this.setState((prevState) => ({
        editComment: {
          ...prevState.editComment,
          [id]: false,
        },
      }));
    } catch (e) {
      console.error(e);
      attachment.content = oldContent;
    }
  };

  private handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      this.sendComment();
    }
  };
}

class State {
  comment: string = '';
  commentLoading: boolean = false;
  editComment: { [key: string]: boolean } = {};
}

interface Props extends ReduxProps, FormicaProps {
  chats: TransactionChatLog[];
  domain: string | undefined;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
});
const mapDispatchToProps = { addPagePath, changePagePath };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(TransactionChatAttachmentComponent));
